import { FC, SyntheticEvent, useState } from 'react';
import { Box, Paper, Tab, Tabs, Typography, useTheme } from '@mui/material';
import { observer } from 'mobx-react-lite';

import { Trans, useTrans } from '@libs/i18n';
import { Assessment, DomainComparisonEntry, OverallResults } from '@libs/types';

import { DomainResultViewModel, OverallResultsViewModel } from '../../types';

import { ChartsTab } from './ChartsTab';
import { DetailedGapsTab } from './DetailedGapsTab';

interface DomainResultsListProps {
  domainResults: DomainResultViewModel[];
  overallResults?: OverallResultsViewModel;
  comparisonOverall?: OverallResults;
  domainComparisonMap?: Map<string, DomainComparisonEntry>;
  activeVersion?: Assessment;
  baselineVersion?: Assessment;
  /** In-progress draft sibling — enables per-domain "Edit" buttons in the gaps tab. */
  draftVersion?: Assessment;
  /** Called when a user clicks "Edit" but no draft exists yet — prompts them to start a new version. */
  onRequestNewVersion?: () => void;
  allVersions?: Assessment[];
  hasMultipleVersions?: boolean;
  compareToId?: string;
  activeVersionId?: string;
  /** Controlled tab index (0 = Overview, 1 = Detailed Gaps). Falls back to internal state if omitted. */
  activeTab?: number;
  onTabChange?: (tab: number) => void;
}

export const DomainResultsList: FC<DomainResultsListProps> = observer(({
  domainResults,
  overallResults,
  comparisonOverall,
  domainComparisonMap,
  activeVersion,
  baselineVersion,
  draftVersion,
  onRequestNewVersion,
  allVersions,
  hasMultipleVersions = false,
  compareToId,
  activeTab,
  onTabChange,
}) => {
  const theme = useTheme();
  const { t } = useTrans();
  const [internalTab, setInternalTab] = useState(0);

  const tab = activeTab ?? internalTab;
  const isComparing = !!baselineVersion;

  const handleTabChange = (_: SyntheticEvent, value: number) => {
    setInternalTab(value);
    onTabChange?.(value);
  };

  return (
    <Paper
      variant="panel"
      elevation={0}
      sx={{ border: `1px solid ${theme.palette.grey[300]}`, backgroundColor: 'white' }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <Typography variant="textLG_medium" sx={{ color: 'text.primary' }}>
              <Trans i18nKey="assessments.overview.detailedDomainBreakdown" />
            </Typography>
            <Typography variant="textSM_regular" sx={{ color: 'text.tertiary' }}>
              {isComparing && activeVersion && baselineVersion
                ? t('assessments.overview.versioning.domainBreakdown.comparingSubtitle', {
                    active: `V${activeVersion.version}`,
                    baseline: `V${baselineVersion.version}`,
                  })
                : t('assessments.overview.comprehensiveBreakdownByDomain')}
            </Typography>
          </Box>
        </Box>

        {/* Tabs */}
        <Tabs
          value={tab}
          onChange={handleTabChange}
          sx={{
            borderBottom: `1px solid ${theme.palette.grey[200]}`,
            minHeight: 36,
            mt: -1,
            '& .MuiTab-root': { minHeight: 36, textTransform: 'none', fontWeight: 500, fontSize: '0.875rem' },
          }}
        >
          <Tab label={<Trans i18nKey="assessments.overview.tabs.overview" />} />
          <Tab label={<Trans i18nKey="assessments.overview.tabs.detailedGaps" />} />
        </Tabs>

        {tab === 0 && overallResults && (
          <ChartsTab
            domainResults={domainResults}
            overallResults={overallResults}
            comparisonOverall={comparisonOverall}
            domainComparisonMap={domainComparisonMap}
            activeVersion={activeVersion}
            baselineVersion={baselineVersion}
            allVersions={allVersions}
          />
        )}

        {tab === 1 && (
          <DetailedGapsTab
            domainResults={domainResults}
            overallResults={overallResults}
            comparisonOverall={comparisonOverall}
            domainComparisonMap={domainComparisonMap}
            activeVersion={activeVersion}
            baselineVersion={baselineVersion}
            draftVersion={draftVersion}
            onRequestNewVersion={onRequestNewVersion}
          />
        )}
      </Box>
    </Paper>
  );
});
